import { Button, Drawer, Form, Input, Select, Space } from "antd";
import { useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import SelectDropdownLgIcon from "../../../../../assets/icons/SelectDropdownLgIcon";
import { useGetDisbursementBankKeywordsQuery } from "../../../../../features/emp-setting/employee/employeeApi";
import useError from "../../../../../hooks/useError";
import { ModalComponentProps } from "../../../../../types";
import {
  selectArrayOption,
  selectSearchOption,
} from "../../../../../utils/utils";
import { FinancialModalType } from "./FinancialInfo";

type Props = {
  handleAdd: (data: Record<string, string | number>) => void;
} & ModalComponentProps<any, FinancialModalType>;

export default function BankAddModal({
  handleClose,
  type,
  data,
  handleAdd,
  ...rest
}: Props) {
  const [form] = Form.useForm();
  const {
    isLoading: isBankLoading,
    isError: isBankError,
    error: bankError,
    data: bankData,
  } = useGetDisbursementBankKeywordsQuery({ params: { status: "active" } });
  useError(isBankError, bankError, "Get bank info fail");

  const onFinish = (values: Record<string, string | number>) => {
    if (type === "BANK_EDIT") {
      handleAdd({
        ...values,
        id: data?.id,
        // is_active: data?.id,
      });
    } else {
      handleAdd({
        ...values,
        id: uuidv4(),
        // is_active: 0,
      });
    }
    handleClose();
  };

  useEffect(() => {
    if (type === "BANK_EDIT" && data) {
      form.setFieldsValue({
        bank_name: data.bank_name,
        bank_id: data.bank_id,
        account_no: data.account_no,
        bank_account_title: data.bank_account_title,
        branch_name: data.branch_name,
        routing_no: data.routing_no,
      });
    }
  }, [type, data, form]);

  return (
    <Drawer
      title={`${
        type === "BANK_EDIT" ? "Edit" : type === "BANK_ADD" ? "Add" : "View"
      } Bank Details`}
      placement="right"
      width={480}
      className="right_site_drawer"
      onClose={handleClose}
      {...rest}
    >
      <Form
        className="custom-form"
        layout="vertical"
        requiredMark="optional"
        size="large"
        onFinish={onFinish}
        form={form}
      >
        <Form.Item
          name="bank_name"
          label="Select Bank"
          className="mb-4 sr-only"
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="bank_id"
          label="Select Bank"
          className="mb-4"
          rules={[
            {
              required: true,
              message: "Please select a bank",
            },
          ]}
        >
          <Select
            placeholder="Select"
            size="large"
            allowClear
            showSearch
            filterOption={selectSearchOption}
            options={selectArrayOption(
              bankData?.data || [],
              "name_en",
              "bank_id"
            )}
            suffixIcon={<SelectDropdownLgIcon />}
            disabled={isBankLoading}
            onChange={(el) => {
              const findBank = bankData?.data?.find(
                (item) => item?.bank_id === el
              );
              form.setFieldsValue({
                bank_name: findBank?.keyword,
              });
            }}
          />
        </Form.Item>
        <Form.Item
          name="account_no"
          label="Bank Account Number"
          className="mb-4"
          rules={[
            {
              validator: async (_: any, value: string) => {
                if (!value) {
                  return Promise.reject(
                    new Error("Please enter bank account number")
                  );
                }

                if (value?.length < 6) {
                  return Promise.reject(
                    new Error("Bank account number should be minimum 06")
                  );
                }

                if (value?.length > 19) {
                  return Promise.reject(
                    new Error("Bank account number should be maximum 19")
                  );
                }

                if (value.startsWith("-")) {
                  return Promise.reject(
                    new Error("Bank account cannot be negative")
                  );
                }

                if (!Number.isInteger(Number(value))) {
                  return Promise.reject(
                    new Error("Bank account number must be a number")
                  );
                }

                const regex = /^[A-Za-z0-9-]{6,34}$/;
                if (!regex.test(value)) {
                  return Promise.reject(
                    new Error("Please enter a valid bank account number")
                  );
                }
              },
            },
          ]}
        >
          <Input size="large" placeholder="Ex: 12345" />
        </Form.Item>
        <Form.Item
          name="bank_account_title"
          label="Bank Account Title"
          className="mb-4"
        >
          <Input size="large" placeholder="Ex: Title" />
        </Form.Item>
        <Form.Item name="branch_name" label="Branch Name" className="mb-4">
          <Input size="large" placeholder="Ex: Branch" />
        </Form.Item>
        <Form.Item name="routing_no" label="Routing No" className="mb-4">
          <Input size="large" placeholder="Ex: 123654" />
        </Form.Item>

        <Space className="w-full mt-2" size={12}>
          <Button
            onClick={handleClose}
            className="normal-dark-button modal-button"
            size="middle"
          >
            Cancel
          </Button>
          {(type === "BANK_ADD" || type === "BANK_EDIT") && (
            <Button
              className="modal-button"
              type="primary"
              htmlType="submit"
              size="middle"
              //   loading={isLoading || updatePortalDepartmentIsLoading}
            >
              {data ? "Update" : "Save"}
            </Button>
          )}
        </Space>
      </Form>
    </Drawer>
  );
}
